import React from 'react';
import { CheckCircle2, Circle, PlayCircle, ChevronDown, ChevronRight, Award } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';

interface ModuleItem {
  id: string;
  title: string;
  progress: number;
  hasQuiz?: boolean;
  quizPassed?: boolean;
}

interface ModuleChecklistProps {
  modules: ModuleItem[];
  currentModuleId?: string;
  onModuleClick?: (moduleId: string) => void;
  defaultOpen?: boolean;
  className?: string;
}

export const ModuleChecklist: React.FC<ModuleChecklistProps> = ({
  modules,
  currentModuleId,
  onModuleClick,
  defaultOpen = false,
  className,
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const getModuleIcon = (module: ModuleItem, isCurrent: boolean) => {
    if (module.progress === 100) {
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    }
    if (isCurrent || module.progress > 0) {
      return <PlayCircle className="h-5 w-5 text-primary" />;
    }
    return <Circle className="h-5 w-5 text-muted-foreground/40" />;
  };

  const getModuleStatus = (module: ModuleItem, isCurrent: boolean) => {
    if (module.progress === 100) return 'completed';
    if (isCurrent) return 'current';
    if (module.progress > 0) return 'in-progress';
    return 'not-started';
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-2 h-auto">
          <span className="text-sm font-medium">
            Module Progress ({modules.filter(m => m.progress === 100).length}/{modules.length})
          </span>
          {isOpen ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="space-y-1 pt-2">
        {modules.map((module, index) => {
          const isCurrent = module.id === currentModuleId;
          const status = getModuleStatus(module, isCurrent);
          
          return (
            <button
              key={module.id}
              onClick={() => onModuleClick?.(module.id)}
              className={cn(
                'w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors',
                status === 'completed' && 'bg-green-500/5 hover:bg-green-500/10',
                status === 'current' && 'bg-primary/10 hover:bg-primary/15 ring-1 ring-primary/30',
                status === 'in-progress' && 'bg-primary/5 hover:bg-primary/10',
                status === 'not-started' && 'hover:bg-muted',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
              )}
            >
              {getModuleIcon(module, isCurrent)}
              
              <div className="flex-1 min-w-0">
                <p className={cn(
                  'text-sm font-medium truncate',
                  status === 'completed' && 'text-green-700 dark:text-green-400',
                  status === 'current' && 'text-primary',
                  status === 'in-progress' && 'text-foreground',
                  status === 'not-started' && 'text-muted-foreground'
                )}>
                  {index + 1}. {module.title}
                </p>
                {module.progress > 0 && module.progress < 100 && (
                  <p className="text-xs text-muted-foreground">{module.progress}% complete</p>
                )}
              </div>
              
              {/* Quiz badge */}
              {module.hasQuiz && (
                <div className={cn(
                  'flex items-center gap-1 px-2 py-0.5 rounded text-xs',
                  module.quizPassed 
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400' 
                    : 'bg-muted text-muted-foreground'
                )}>
                  <Award className="h-3 w-3" />
                  {module.quizPassed ? 'Passed' : 'Quiz'}
                </div>
              )}
            </button>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ModuleChecklist;
